import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from "recharts";
import { TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PerformanceChart({ currency }) {
  const [data, setData] = useState(null);

  const fetchPerf = useCallback(async () => {
    try {
      const res = await fetch(`${API}/investments/performance?currency=${currency}`, {
        credentials: 'include'
      });
      if (res.ok) setData(await res.json());
    } catch {
      // silent
    }
  }, [currency]);

  useEffect(() => {
    fetchPerf();
  }, [fetchPerf]);

  const series = data?.series || [];
  const currentMV = data?.current_market_value ?? 0;

  return (
    <Card data-testid="performance-chart-card" className="border border-border mb-8">
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5" strokeWidth={1.5} />
          Performance Over Time
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground">
          Cumulative invested and realized P&L from transaction history · Current market value: {currency} {currentMV.toFixed(2)}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        {series.length === 0 ? (
          <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
            No investment history yet. Record a Buy or Sell to start tracking performance.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3, 31.8%, 91.4%)" />
              <XAxis dataKey="date" stroke="hsl(215.4, 16.3%, 46.9%)" />
              <YAxis stroke="hsl(215.4, 16.3%, 46.9%)" />
              <Tooltip
                formatter={(value, name) => [`${currency} ${value.toFixed(2)}`, name]}
                labelClassName="text-foreground"
                contentStyle={{
                  background: "white",
                  border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                  borderRadius: 6
                }}
              />
              <Legend />
              <ReferenceLine
                y={currentMV}
                stroke="hsl(43, 74%, 50%)"
                strokeDasharray="4 4"
                label={{ value: `MV today: ${currency} ${currentMV.toFixed(0)}`, position: "insideTopRight", fontSize: 11 }}
              />
              <Line
                type="monotone"
                dataKey="invested"
                stroke="hsl(221.2, 83.2%, 53.3%)"
                strokeWidth={2}
                name="Cost basis"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="realized"
                stroke="hsl(142.1, 76.2%, 36.3%)"
                strokeWidth={2}
                name="Realized P&L"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default PerformanceChart;
